import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { IoExpand, IoContract, IoDownload } from "react-icons/io5";

const ImagePreviewModal = ({ isOpen, onClose, imageUrl }) => {
    const isPdf = imageUrl?.endsWith(".pdf");
    const [isZoomed, setIsZoomed] = useState(false);
    const [imageScale, setImageScale] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const handleCloseModal = () => {
        setImageScale(1);
        setImagePosition({ x: 0, y: 0 });
        setIsZoomed(false);
        setIsDragging(false);
        onClose();
    };

    const handleZoomToggle = () => {
        if (isZoomed) {
            setImageScale(1);
            setImagePosition({ x: 0, y: 0 });
            setIsZoomed(false);
        } else {
            setImageScale(2);
            setIsZoomed(true);
        }
    };

    const handleMouseDown = (e) => {
        if (isZoomed) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - imagePosition.x,
                y: e.clientY - imagePosition.y,
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && isZoomed) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleDownloadImage = () => {
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `image_${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleCloseModal}
            aria-labelledby="document-preview-modal"
            aria-describedby="modal-to-preview-document"
            closeAfterTransition
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    backgroundColor: "rgba(0, 0, 0, 0.85)",
                    backdropFilter: "blur(8px)",
                    outline: "none",
                    position: "relative",
                    padding: "20px",
                    transition: "all 0.3s ease-out",
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onClick={handleCloseModal}
            >
                {/* Enhanced Controls Header */}
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: 70, sm: 20 },
                        left: 0,
                        right: 0,
                        display: "flex",
                        justifyContent: { xs: "flex-start", sm: "space-between" },
                        alignItems: "center",
                        px: 3,
                        zIndex: 10,
                    }}
                >
                    {!isPdf && (
                        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 2 }}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleZoomToggle();
                                }}
                                sx={{
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    backdropFilter: "blur(8px)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                        transform: "scale(1.1)",
                                    },
                                }}
                                title={isZoomed ? "Zoom Out" : "Zoom In"}
                            >
                                {isZoomed ? <IoContract size={20} /> : <IoExpand size={20} />}
                            </IconButton>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadImage();
                                }}
                                sx={{
                                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                                    color: "white",
                                    backdropFilter: "blur(8px)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                        transform: "scale(1.1)",
                                    },
                                }}
                                title="Download Image"
                            >
                                <IoDownload size={20} />
                            </IconButton>
                        </Box>
                    )}
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCloseModal();
                        }}
                        sx={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            backdropFilter: "blur(8px)",
                            transition: "all 0.2s ease",
                            marginLeft: { xs: 0, sm: "auto" },
                            "&:hover": {
                                backgroundColor: "rgba(220, 38, 38, 0.7)",
                                transform: "scale(1.1)",
                            },
                        }}
                        title="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content Container */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        borderRadius: "16px",
                        overflow: "hidden",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(8px)",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                        padding: "8px",
                        animation: "slideIn 0.3s ease-out",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isPdf ? (
                        <iframe
                            src={`${imageUrl}#toolbar=0`}
                            title="PDF Preview"
                            style={{
                                width: "80vw",
                                height: "85vh",
                                border: "none",
                                borderRadius: "12px",
                            }}
                        />
                    ) : (
                        <img
                            src={imageUrl}
                            alt="Preview"
                            onMouseDown={handleMouseDown}
                            onDoubleClick={handleZoomToggle}
                            draggable={false}
                            style={{
                                maxWidth: "90vw",
                                maxHeight: "85vh",
                                objectFit: "contain",
                                borderRadius: "12px",
                                transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                                transformOrigin: "center center",
                                transition: "transform 0.3s ease",
                                cursor: isZoomed ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                            }}
                        />
                    )}
                </Box>

                {/* Info Bar */}
                {!isPdf && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 20,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            px: 3,
                            py: 1,
                            borderRadius: "20px",
                            backdropFilter: "blur(8px)",
                            fontSize: "14px",
                            pointerEvents: "none",
                        }}
                    >
                        {isZoomed ? "Click and drag to pan • Double-click to zoom out" : "Double-click to zoom in"}
                    </Box>
                )}

                {/* Enhanced CSS Animations */}
                <style jsx global>{`
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: scale(0.9) translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1) translateY(0);
                        }
                    }
                `}</style>
            </Box>
        </Modal>
    );
};

export default ImagePreviewModal;
